import { useRef, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { AssignBonus } from "features/bonus2/components/player-bonuses/AssignBonus"

import { _currentTenant, isBonusV2Tenant } from "config/featureFlags"

import { CustomBonusTab, TabHandle } from "./CustomBonusTab"
import { DepositBonusTab } from "./DepositBonusTab"
import { ReloadTab } from "./ReloadTab"

type ModalAddUserBonusProps = {
  onClose: () => void
  open: boolean
  userId: string
  coin?: string
  onSuccess: () => void
}

type ActiveTab = "CUSTOM" | "ASSIGN" | "RELOAD"

const TAB_KEYS: ActiveTab[] = ["CUSTOM", "ASSIGN", "RELOAD"]

export const ModalAddUserBonus = ({ open, onClose, userId, coin, onSuccess }: ModalAddUserBonusProps) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>("CUSTOM")
  const [reloadHasActive, setReloadHasActive] = useState(false)
  const { t } = useTranslation("translation", { useSuspense: false })

  const assignsFromBonus2 = isBonusV2Tenant()

  const TAB_LABELS: Record<ActiveTab, string> = {
    CUSTOM: "addUserBonus.tabCustomBonus",
    ASSIGN: assignsFromBonus2 ? "addUserBonus.tabAssignBonus" : "addUserBonus.tabDepositBonusOffer",
    RELOAD: "addUserBonus.tabReload"
  }

  const customRef = useRef<TabHandle>(null)
  const assignRef = useRef<TabHandle>(null)
  const reloadRef = useRef<TabHandle>(null)

  const activeRef = activeTab === "CUSTOM" ? customRef : activeTab === "ASSIGN" ? assignRef : reloadRef
  const isSubmitting = activeRef.current?.isSubmitting ?? false
  const hideFooter = (activeTab === "RELOAD" && reloadHasActive) || (activeTab === "ASSIGN" && assignsFromBonus2)

  const handleSaveClick = () => {
    activeRef.current?.handleSubmit()
  }

  const renderTab = () => {
    switch (activeTab) {
      case "CUSTOM":
        return <CustomBonusTab ref={customRef} userId={userId} coin={coin} onSuccess={onSuccess} onClose={onClose} />
      case "ASSIGN":
        return assignsFromBonus2 ? (
          <AssignBonus tenant={_currentTenant ?? ""} fixedUserId={userId} onAssigned={onSuccess} />
        ) : (
          <DepositBonusTab ref={assignRef} userId={userId} open={open} onSuccess={onSuccess} onClose={onClose} />
        )
      case "RELOAD":
        return (
          <ReloadTab
            ref={reloadRef}
            userId={userId}
            open={open}
            onSuccess={onSuccess}
            onClose={onClose}
            onActiveChange={setReloadHasActive}
          />
        )
    }
  }

  return (
    <Modal open={open} handler={onClose}>
      <div className="relative flex flex-col justify-between modal-box w-auto">
        <div className="flex justify-center">
          <div className="btn-group">
            {TAB_KEYS.map((key) => (
              <button
                key={key}
                className={clsx("btn", { "btn-active": activeTab === key })}
                type="button"
                onClick={() => setActiveTab(key)}
              >
                {t(TAB_LABELS[key])}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col pt-5">
          {renderTab()}

          {!hideFooter && (
            <div className="modal-action flex items-center space-x-4 mt-4">
              <button className="btn btn-secondary flex-1" onClick={onClose} disabled={isSubmitting}>
                {t("addUserBonus.cancel")}
              </button>
              <button
                className={clsx("btn btn-primary flex-1", { loading: isSubmitting })}
                onClick={handleSaveClick}
                disabled={isSubmitting}
                type="button"
              >
                {t("addUserBonus.save")}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
